import { ToastType } from '@components/toast';
import { SystemAlertPatchRequestBody } from '@lio/api-types';
import { alertsQueryKeys } from '@services/alert/keys';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { getApiErrorMessage } from '@utils/getApiErrorMessage';
import { useSnackbar } from 'notistack';

export const useUpdateSystemAlert = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const mutationFn = async ({
    systemAlertId,
    updateParams,
  }: {
    systemAlertId: string;
    updateParams: SystemAlertPatchRequestBody;
  }) => {
    if (updateParams.startAt && updateParams.endAt && new Date(updateParams.startAt) >= new Date(updateParams.endAt)) {
      throw new Error('startAt must be less than endAt');
    }
    const url = `system-management/system-alert/${systemAlertId}`;

    return client.patch(url, {
      json: {
        ...updateParams,
      },
    });
  };

  const result = useMutation({
    mutationFn,
    onError: async (error) => {
      const fallback = error instanceof Error ? error.message : 'An error occurred.';
      const message = await getApiErrorMessage(error, fallback);
      enqueueSnackbar(`Update Failed: ${message}`, {
        variant: ToastType.Error,
        persist: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...alertsQueryKeys.all] }).then(() => {
        enqueueSnackbar('System Alert Successfully Updated', {
          variant: ToastType.Success,
        });
      });
    },
  });
  return { ...result, updateSystemAlertAsync: result.mutateAsync };
};
